import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { vesselAPI, usersAPI, logisticsAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { useAuth } from "../contexts/AuthContext";
import { Container, AlertTriangle, Download, RefreshCw, CheckCircle, Package, Wrench, Clock, X, Loader, PenLine, ArrowUpCircle, ArrowDownCircle, Trash2, List } from "lucide-react";
import { fmtDate, fmtDateTime } from "../utils/dateUtils";

const SIZE_LABELS = {
  count_5L: "5L", count_10L: "10L", count_20L: "20L",
  count_30L: "30L", count_40L: "40L",
  egg_tray: "Egg Tray", banana_tray: "Banana Tray"
};

function VesselBar({ label, out, returned }) {
  const missing = Math.max(0, out - returned);
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-8 font-bold text-[#8A7E73]">{label}</span>
      <span className="text-[#3A322C] w-6 text-right">{out}</span>
      <div className="flex-1 h-2 bg-[#F4F3F0] rounded-full overflow-hidden">
        <div className="h-full bg-[#637C64] rounded-full" style={{ width: out > 0 ? `${(returned / out) * 100}%` : "0%" }} />
      </div>
      <span className="w-6 text-right text-[#637C64]">{returned}</span>
      {missing > 0 && <span className="w-6 text-right font-bold text-[#C2624E]">-{missing}</span>}
    </div>
  );
}

export default function VesselManagement() {
  const [searchParams] = useSearchParams();
  const [summary, setSummary] = useState([]);
  const [report, setReport] = useState(null);
  const { kitchens: ctxKitchens, selectedKitchen, changeKitchen: setSelectedKitchen } = useKitchen();
  const { user } = useAuth();
  const [kitchens, setKitchens] = useState(ctxKitchens || []);
  // Fallback: direct fetch if context kitchens still empty
  useEffect(() => {
    if (ctxKitchens && ctxKitchens.length > 0) { setKitchens(ctxKitchens); return; }
    const _t = setTimeout(() => {
      import("../utils/api").then(({ usersAPI }) => {
        usersAPI.getKitchens().then(r => {
          const ks = r.data || [];
          if (ks.length > 0) setKitchens(ks);
        }).catch(() => {});
      });
    }, 800);
    return () => clearTimeout(_t);
  }, [ctxKitchens]); // eslint-disable-line react-hooks/exhaustive-deps
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState(searchParams.get("tab") || "summary");
  // Sync tab from URL
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setActiveView(t);
  }, [searchParams]);

  // ── Inventory state ──
  const [inventory, setInventory] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [stockLogs, setStockLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // ── Movement log state ──
  const [movementLogs, setMovementLogs] = useState([]);
  const [movLoading, setMovLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteMsg, setDeleteMsg] = useState("");

  // Opening stock modal
  const [openingModal, setOpeningModal] = useState(null); // {size, label, current_stock, opening_stock, kitchen_id}
  const [openingForm, setOpeningForm] = useState({ opening_stock: "", notes: "" });
  const [openingLoading, setOpeningLoading] = useState(false);
  const [openingErr, setOpeningErr] = useState("");

  // Reduce stock modal
  const [reduceModal, setReduceModal] = useState(null); // {size, label, current_stock, kitchen_id}
  const [reduceForm, setReduceForm] = useState({ quantity: "", reason: "Damaged", notes: "" });
  const [reduceLoading, setReduceLoading] = useState(false);
  const [reduceErr, setReduceErr] = useState("");
  const [reduceSuccess, setReduceSuccess] = useState("");

  const REMOVAL_REASONS = ["Damaged", "Lost", "Decommissioned / Scrapped", "Other"];

  // ── Vessel Entry state ──
  const VESSEL_SIZES_ENTRY = [
    { key: "count_5L",    label: "5 Litre" },
    { key: "count_10L",   label: "10 Litre" },
    { key: "count_20L",   label: "20 Litre" },
    { key: "count_30L",   label: "30 Litre" },
    { key: "count_40L",   label: "40 Litre" },
    { key: "egg_tray",    label: "Egg Tray" },
    { key: "banana_tray", label: "Banana Tray" },
  ];
  const BLANK_ENTRY = {
    direction: "outward",
    route_id: "",
    route_number: "",
    vehicle_number: "",
    notes: "",
    reason: "",
    count_5L: "", count_10L: "", count_20L: "", count_30L: "", count_40L: "",
    egg_tray: "", banana_tray: "",
  };
  const [entryForm, setEntryForm] = useState(BLANK_ENTRY);
  const [entryLoading, setEntryLoading] = useState(false);
  const [entryResult, setEntryResult] = useState(null);
  const [entryErr, setEntryErr] = useState("");
  const [routes, setRoutes] = useState([]);
  const [routeSearch, setRouteSearch] = useState("");

  // Admin access gate
  const ADMIN_ROLE_NAMES = ["Super Admin", "Administration", "Semi- Admin"];
  const canVesselEdit = !!(user?.is_admin || ADMIN_ROLE_NAMES.includes(user?.role_name) || user?.permissions?.vessel_edit);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        vesselAPI.getSummary(selectedKitchen ? { kitchen_id: selectedKitchen } : {}),
        vesselAPI.getBiweeklyReport(selectedKitchen ? { kitchen_id: selectedKitchen } : {})
      ]);
      setSummary(s.data || []);
      setReport(r.data);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const fetchInventory = async () => {
    setInvLoading(true);
    try {
      const r = await vesselAPI.getInventory(selectedKitchen ? { kitchen_id: selectedKitchen } : {});
      setInventory(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setInvLoading(false);
  };

  const fetchStockLogs = async () => {
    setLogsLoading(true);
    try {
      const r = await vesselAPI.getStockLogs(selectedKitchen ? { kitchen_id: selectedKitchen } : {});
      setStockLogs(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLogsLoading(false);
  };

  const fetchMovementLogs = async () => {
    setMovLoading(true);
    try {
      const r = await vesselAPI.getLogs(selectedKitchen ? { kitchen_id: selectedKitchen } : {});
      setMovementLogs(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setMovLoading(false);
  };

  const handleDeleteLogEntry = async (entry) => {
    if (!window.confirm(`Delete this vessel entry?\n\nRoute: ${entry.route_number || '?'} | ${entry.direction} | ${entry.source}\n\nThis cannot be undone.`)) return;
    setDeletingId(entry.id); setDeleteMsg("");
    try {
      await vesselAPI.deleteLogEntry(entry.id);
      setDeleteMsg("Entry deleted.");
      await Promise.all([fetchMovementLogs(), fetchData()]);
    } catch (e) { setDeleteMsg("Error: " + formatError(e)); }
    setDeletingId(null);
  };

  // Fetch routes for the Entry tab (admin only)
  useEffect(() => {
    if (activeView === "entry" && canVesselEdit && routes.length === 0) {
      logisticsAPI.getRoutes().then(r => setRoutes(r.data || [])).catch(() => {});
    }
  }, [activeView]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVesselEntry = async () => {
    setEntryErr("");
    // Frontend validation before hitting API
    if (!entryForm.route_number.trim()) {
      setEntryErr("Route number is required. Select a valid route from the list.");
      return;
    }
    const routeValid = routes.some(r => String(r.route_number) === entryForm.route_number.trim());
    if (!routeValid) {
      setEntryErr(`Route '${entryForm.route_number}' is not in Route Master. Only active routes are allowed.`);
      return;
    }
    if (!entryForm.reason.trim()) {
      setEntryErr("Reason for correction is required.");
      return;
    }
    const counts = {};
    let total = 0;
    for (const s of VESSEL_SIZES_ENTRY) {
      const v = parseInt(entryForm[s.key]) || 0;
      counts[s.key] = v;
      total += v;
    }
    if (total === 0) { setEntryErr("Enter at least one vessel count greater than zero"); return; }
    setEntryLoading(true); setEntryResult(null);
    try {
      const payload = {
        direction: entryForm.direction,
        kitchen_id: selectedKitchen || "",
        route_id: entryForm.route_id || null,
        route_number: entryForm.route_number.trim(),
        vehicle_number: entryForm.vehicle_number,
        notes: entryForm.notes,
        reason: entryForm.reason.trim(),
        ...counts,
      };
      const r = await vesselAPI.logEntry(payload);
      setEntryResult(r.data);
      setEntryForm({ ...BLANK_ENTRY, direction: entryForm.direction });
      setRouteSearch("");
      fetchData(); // refresh summary
    } catch (e) { setEntryErr(formatError(e)); }
    setEntryLoading(false);
  };

  // Fetch inventory when tab switches to inventory/history/movement
  useEffect(() => {
    if (activeView === "inventory") fetchInventory();
    if (activeView === "history") { fetchInventory(); fetchStockLogs(); }
    if (activeView === "movement") fetchMovementLogs();
  }, [activeView, selectedKitchen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetOpeningStock = async () => {
    setOpeningLoading(true); setOpeningErr("");
    const qty = parseInt(openingForm.opening_stock);
    if (!qty && qty !== 0) { setOpeningErr("Enter a valid quantity"); setOpeningLoading(false); return; }
    if (qty < 0) { setOpeningErr("Opening stock cannot be negative"); setOpeningLoading(false); return; }
    try {
      await vesselAPI.setOpeningStock({
        size: openingModal.size,
        kitchen_id: selectedKitchen || "",
        opening_stock: qty,
        notes: openingForm.notes,
      });
      setOpeningModal(null);
      fetchInventory();
    } catch (e) { setOpeningErr(formatError(e)); }
    setOpeningLoading(false);
  };

  const handleReduceStock = async () => {
    setReduceLoading(true); setReduceErr(""); setReduceSuccess("");
    const qty = parseInt(reduceForm.quantity);
    if (!qty || qty <= 0) { setReduceErr("Enter a valid quantity (must be > 0)"); setReduceLoading(false); return; }
    if (qty > reduceModal.current_stock) {
      setReduceErr(`Cannot remove ${qty} — only ${reduceModal.current_stock} currently in stock`);
      setReduceLoading(false); return;
    }
    try {
      const r = await vesselAPI.reduceStock({
        size: reduceModal.size,
        kitchen_id: selectedKitchen || "",
        quantity: qty,
        reason: reduceForm.reason,
        notes: reduceForm.notes,
      });
      setReduceSuccess(r.data?.message || "Stock reduced successfully");
      setTimeout(() => { setReduceModal(null); setReduceSuccess(""); }, 1500);
      fetchInventory();
    } catch (e) { setReduceErr(formatError(e)); }
    setReduceLoading(false);
  };

  const hasMissing = (route) => Object.values(route.missing || {}).some(v => v > 0);

  const totalMissing = summary.reduce((acc, route) => {
    Object.entries(route.missing || {}).forEach(([k, v]) => { acc[k] = (acc[k] || 0) + v; });
    return acc;
  }, {});

  const anyMissing = Object.values(totalMissing).some(v => v > 0);

  const ACTION_COLORS = {
    opening_stock_set: "bg-blue-100 text-blue-700",
    opening_stock_adjustment: "bg-purple-100 text-purple-700",
    stock_reduction: "bg-[#FBEAE7] text-[#C2624E]",
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="vessel-management">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Vessel Management</h1>
        <div className="flex gap-2">
          <button onClick={() => { fetchData(); if (activeView === "inventory") fetchInventory(); }} className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0] flex items-center gap-1">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Alert Banner */}
      {anyMissing && (
        <div className="bg-[#FBEAE7] border border-[#C2624E]/30 rounded-xl px-5 py-4 flex items-center gap-3">
          <AlertTriangle size={20} className="text-[#C2624E] flex-shrink-0" />
          <div>
            <p className="font-bold text-[#C2624E]">Missing Vessels Detected</p>
            <p className="text-sm text-[#C2624E]/80">
              {Object.entries(totalMissing).filter(([, v]) => v > 0).map(([k, v]) => `${SIZE_LABELS[k]}: ${v}`).join(", ")}
            </p>
          </div>
        </div>
      )}

      {/* Route-wise Missing Table */}
      {anyMissing && (
        <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
          <div className="px-5 py-3 bg-[#FBEAE7] border-b border-[#C2624E]/20">
            <p className="font-bold text-[#C2624E] text-sm">Route-wise Missing Vessels</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F3F0]">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">Route</th>
                  {Object.entries(SIZE_LABELS).map(([key, label]) => (
                    <th key={key} className="text-center px-3 py-3 text-xs font-bold uppercase text-[#5C5147] whitespace-nowrap">{label}</th>
                  ))}
                  <th className="text-center px-3 py-3 text-xs font-bold uppercase text-[#C2624E]">Total Missing</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F4F3F0]">
                {summary.filter(hasMissing).map((route, i) => {
                  const totalMissingRoute = Object.values(route.missing || {}).reduce((s, v) => s + v, 0);
                  return (
                    <tr key={`idx-${i}`} className="hover:bg-[#FAFAF8]">
                      <td className="px-4 py-3 font-bold text-[#E8956D]">Route {route.route_number}</td>
                      {Object.keys(SIZE_LABELS).map(key => (
                        <td key={key} className="px-3 py-3 text-center">
                          {(route.missing?.[key] || 0) > 0
                            ? <span className="font-bold text-[#C2624E] text-sm">{route.missing[key]}</span>
                            : <span className="text-[#C4BAAF] text-xs">—</span>}
                        </td>
                      ))}
                      <td className="px-3 py-3 text-center">
                        <span className="font-extrabold text-[#C2624E] bg-[#FBEAE7] px-2 py-0.5 rounded-full">{totalMissingRoute}</span>
                      </td>
                    </tr>
                  );
                })}
                {/* Total row */}
                <tr className="bg-[#F4F3F0]">
                  <td className="px-4 py-3 font-bold text-[#3A322C]">TOTAL</td>
                  {Object.keys(SIZE_LABELS).map(key => (
                    <td key={key} className="px-3 py-3 text-center font-bold text-[#3A322C]">
                      {totalMissing[key] > 0 ? totalMissing[key] : <span className="text-[#C4BAAF]">—</span>}
                    </td>
                  ))}
                  <td className="px-3 py-3 text-center font-extrabold text-[#C2624E]">
                    {Object.values(totalMissing).reduce((s, v) => s + v, 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-[#E5E1DB] overflow-x-auto">
        {[
          ...(canVesselEdit ? [{ id: "entry", label: "Admin Correction", icon: PenLine }] : []),
          { id: "summary",   label: "Current Summary",  icon: Container  },
          { id: "movement",  label: "Movement Log",     icon: List       },
          { id: "inventory", label: "Inventory & Stock", icon: Package   },
          { id: "biweekly",  label: "Bi-Weekly Report",  icon: Download  },
          { id: "history",   label: "Stock History",     icon: Clock     },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveView(t.id)}
            className={`px-4 py-2.5 text-sm font-semibold flex items-center gap-1.5 whitespace-nowrap transition-colors -mb-px ${activeView === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            <t.icon size={14} />{t.label}
          </button>
        ))}
      </div>

      {loading && (activeView === "summary" || activeView === "biweekly") ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-[#E8956D] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <>
          {/* ── ADMIN CORRECTION (was "Vessel Entry") ── */}
          {activeView === "entry" && (
            !canVesselEdit ? (
              <div className="bg-[#FBEAE7] border border-[#C2624E]/20 rounded-2xl px-6 py-10 text-center max-w-md">
                <AlertTriangle size={32} className="mx-auto mb-3 text-[#C2624E]" />
                <p className="font-bold text-[#C2624E]">Access Denied</p>
                <p className="text-sm text-[#8A7E73] mt-1">Admin access or vessel_edit permission required.</p>
              </div>
            ) : (
            <div className="space-y-4 max-w-2xl" data-testid="admin-correction-panel">
              {/* Context banner */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
                <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-amber-800">Admin Correction Only</p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Use this to correct a movement the Security Gate missed or entered incorrectly.
                    This writes to the same vessel log as gate entries, tagged as <code className="bg-amber-100 px-1 rounded">admin_correction</code>.
                    It is NOT a substitute for the daily gate process.
                  </p>
                </div>
              </div>

              {/* Direction toggle */}
              <div className="flex gap-2 p-1 bg-[#F4F3F0] rounded-xl w-fit">
                {[
                  { val: "outward", label: "Outward (Sent Out)", icon: ArrowUpCircle, color: "text-[#E8956D]" },
                  { val: "inward",  label: "Inward (Returned)",  icon: ArrowDownCircle, color: "text-[#637C64]" },
                ].map(d => (
                  <button key={d.val} onClick={() => { setEntryForm(f => ({ ...f, direction: d.val })); setEntryResult(null); setEntryErr(""); }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors ${entryForm.direction === d.val ? `bg-white shadow-sm ${d.color}` : "text-[#8A7E73] hover:text-[#5C5147]"}`}
                    data-testid={`direction-${d.val}`}>
                    <d.icon size={16} /> {d.label}
                  </button>
                ))}
              </div>

              <div className="bg-white border border-[#E5E1DB] rounded-2xl p-5 space-y-4 shadow-sm">
                {/* Route & Vehicle */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">
                      Route Number <span className="text-[#C2624E]">*</span>
                    </label>
                    <input
                      type="text"
                      list="vessel-route-list"
                      placeholder="Type route number..."
                      value={routeSearch || entryForm.route_number}
                      onChange={e => {
                        const v = e.target.value;
                        setRouteSearch(v);
                        setEntryErr("");
                        const match = routes.find(r => String(r.route_number) === v);
                        if (match) {
                          setEntryForm(f => ({ ...f, route_id: match.id, route_number: String(match.route_number), vehicle_number: match.driver_vehicle_number || match.assigned_vehicle || f.vehicle_number }));
                          setRouteSearch(String(match.route_number));
                        } else {
                          setEntryForm(f => ({ ...f, route_id: "", route_number: v }));
                        }
                      }}
                      className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none ${
                        entryForm.route_number && !routes.some(r => String(r.route_number) === entryForm.route_number)
                          ? "border-[#C2624E] bg-[#FBEAE7]"
                          : entryForm.route_number
                          ? "border-[#637C64] bg-[#EDF2EE]"
                          : "border-[#E5E1DB] focus:border-[#E8956D]"
                      }`}
                      data-testid="entry-route-input"
                    />
                    <datalist id="vessel-route-list">
                      {routes.map(r => <option key={r.id} value={String(r.route_number)} />)}
                    </datalist>
                    {entryForm.route_number && !routes.some(r => String(r.route_number) === entryForm.route_number) && (
                      <p className="text-xs text-[#C2624E] mt-1">Route not in Route Master — only active routes allowed</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Vehicle Number</label>
                    <input
                      type="text" placeholder="e.g. TS08AB1234"
                      value={entryForm.vehicle_number}
                      onChange={e => setEntryForm(f => ({ ...f, vehicle_number: e.target.value }))}
                      className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    />
                  </div>
                </div>

                {/* Vessel Counts */}
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-3 block">
                    Vessel Counts — {entryForm.direction === "outward" ? "Correcting Outward" : "Correcting Inward Return"}
                  </label>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-3">
                    {VESSEL_SIZES_ENTRY.map(({ key, label }) => (
                      <div key={key} className="text-center">
                        <label className="text-xs text-[#8A7E73] block mb-1 font-medium">{label}</label>
                        <input
                          type="number" min="0" step="1" placeholder="0"
                          value={entryForm[key]}
                          onChange={e => setEntryForm(f => ({ ...f, [key]: e.target.value }))}
                          className={`w-full border rounded-lg px-2 py-3 text-sm text-center font-bold outline-none focus:border-[#E8956D] ${parseInt(entryForm[key]) > 0 ? (entryForm.direction === "outward" ? "border-[#E8956D] bg-[#FDF4EB]" : "border-[#637C64] bg-[#EDF2EE]") : "border-[#E5E1DB]"}`}
                          data-testid={`entry-${key}`}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-[#8A7E73] mt-2">
                    Total: <strong className={VESSEL_SIZES_ENTRY.reduce((s, si) => s + (parseInt(entryForm[si.key]) || 0), 0) > 0 ? "text-[#E8956D]" : "text-[#C4BAAF]"}>
                      {VESSEL_SIZES_ENTRY.reduce((s, si) => s + (parseInt(entryForm[si.key]) || 0), 0)} vessel(s)
                    </strong>
                  </p>
                </div>

                {/* Reason — mandatory */}
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">
                    Reason for Correction <span className="text-[#C2624E]">*</span>
                  </label>
                  <textarea rows={2}
                    placeholder="e.g. Gate missed scanning route 5 on morning shift, correcting count..."
                    value={entryForm.reason}
                    onChange={e => setEntryForm(f => ({ ...f, reason: e.target.value }))}
                    className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none"
                    data-testid="entry-reason"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Additional Notes (optional)</label>
                  <input type="text" placeholder="Any additional context..."
                    value={entryForm.notes} onChange={e => setEntryForm(f => ({ ...f, notes: e.target.value }))}
                    className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" />
                </div>

                {entryErr && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-xl px-4 py-3 text-sm" data-testid="entry-error">{entryErr}</div>}

                {entryResult && (
                  <div className={`rounded-xl px-4 py-4 text-sm font-medium ${entryResult.missing_total > 0 ? "bg-[#FDF4EB] border border-[#D98032]/30 text-[#D98032]" : "bg-[#EDF2EE] border border-[#637C64]/30 text-[#637C64]"}`}>
                    <p className="font-bold mb-1">{entryResult.message}</p>
                    {entryResult.missing_total > 0 && entryResult.missing && (
                      <p className="text-xs">Missing: {Object.entries(entryResult.missing).map(([k, v]) => `${k.replace("count_","")}: ${v}`).join(", ")}</p>
                    )}
                  </div>
                )}

                <button onClick={handleVesselEntry} disabled={entryLoading}
                  className={`w-full rounded-xl py-3 font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60 ${entryForm.direction === "outward" ? "bg-[#E8956D] text-[#3A322C] hover:bg-[#D48661]" : "bg-[#637C64] text-white hover:bg-[#4F6450]"}`}
                  data-testid="submit-vessel-entry">
                  {entryLoading ? <Loader size={15} className="animate-spin" /> : (entryForm.direction === "outward" ? <ArrowUpCircle size={15} /> : <ArrowDownCircle size={15} />)}
                  {entryLoading ? "Saving..." : (entryForm.direction === "outward" ? "Submit Outward Correction" : "Submit Inward Correction")}
                </button>
              </div>
            </div>
            )
          )}

          {/* ── CURRENT SUMMARY ── */}
          {activeView === "summary" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {summary.length === 0 ? (
                <div className="col-span-3 text-center py-16 text-[#8A7E73]">
                  <Container size={48} className="mx-auto mb-4 opacity-30" />
                  <p>No outstanding vessels</p>
                </div>
              ) : summary.map((route, i) => (
                <div key={`idx-${i}`} className={`bg-white border rounded-xl p-4 shadow-sm ${hasMissing(route) ? "border-[#C2624E]" : "border-[#E5E1DB]"}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-bold text-[#3A322C] text-base">Route {route.route_number}</p>
                      {route.kitchen_name && (
                        <p className="text-xs text-[#8A7E73] mt-0.5">{route.kitchen_name}</p>
                      )}
                      {route.vehicle_number && (
                        <p className="text-xs font-mono bg-[#F4F3F0] text-[#5C5147] px-1.5 py-0.5 rounded mt-1 inline-block">{route.vehicle_number}</p>
                      )}
                    </div>
                    {hasMissing(route) ? <AlertTriangle size={18} className="text-[#C2624E] shrink-0" /> : <CheckCircle size={18} className="text-[#637C64] shrink-0" />}
                  </div>

                  {/* Totals summary row */}
                  <div className="flex gap-3 text-xs mb-3 bg-[#FAFAF8] rounded-lg px-3 py-2">
                    <span className="text-[#5C5147]">Out: <strong className="text-[#3A322C]">{route.grand_total_out || 0}</strong></span>
                    <span className="text-[#637C64]">Returned: <strong>{route.grand_total_returned || 0}</strong></span>
                    {(route.grand_total_missing || 0) > 0 && (
                      <span className="text-[#C2624E] font-bold">Missing: {route.grand_total_missing}</span>
                    )}
                    {route.entry_count > 1 && (
                      <span className="text-[#C4BAAF] ml-auto">{route.entry_count} trips</span>
                    )}
                  </div>

                  {/* Per-size bars */}
                  <div className="space-y-2">
                    {Object.entries(SIZE_LABELS).map(([key, label]) => (
                      <VesselBar key={key} label={label} out={route.total_out?.[key] || 0} returned={route.total_returned?.[key] || 0} />
                    ))}
                  </div>
                  {hasMissing(route) && (
                    <div className="mt-3 bg-[#FBEAE7] rounded-lg px-3 py-2 text-xs text-[#C2624E] font-bold">
                      Missing: {Object.entries(route.missing).filter(([, v]) => v > 0).map(([k, v]) => `${SIZE_LABELS[k]}: ${v}`).join(", ")}
                    </div>
                  )}
                  {route.last_outward && (
                    <p className="text-[10px] text-[#C4BAAF] mt-2">Last outward: {route.last_outward}</p>
                  )}
                </div>
              ))}
            </div>
          )}


          {/* ── MOVEMENT LOG ── */}
          {activeView === "movement" && (
            <div className="space-y-3" data-testid="movement-log-tab">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-bold text-[#3A322C]">Vessel Movement Log</h2>
                  <p className="text-xs text-[#8A7E73]">All outward / inward vessel movements. Admin can delete incorrect entries.</p>
                </div>
                <button onClick={fetchMovementLogs} disabled={movLoading} className="flex items-center gap-1 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]">
                  <RefreshCw size={13} className={movLoading ? "animate-spin" : ""} /> Refresh
                </button>
              </div>

              {deleteMsg && (
                <div className={`text-sm px-4 py-2.5 rounded-xl ${deleteMsg.startsWith("Error") ? "bg-[#FBEAE7] text-[#C2624E]" : "bg-[#EDF2EE] text-[#637C64]"}`}>
                  {deleteMsg}
                </div>
              )}

              {movLoading ? (
                <div className="flex justify-center py-12"><RefreshCw size={22} className="animate-spin text-[#E8956D]" /></div>
              ) : movementLogs.length === 0 ? (
                <div className="text-center py-12 text-[#8A7E73] bg-white border border-[#E5E1DB] rounded-xl">
                  <List size={36} className="mx-auto mb-3 opacity-20" />
                  <p>No vessel movements recorded yet.</p>
                </div>
              ) : (
                <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F4F3F0]">
                        <tr>
                          {["Date / Time", "Route", "Direction", "Source", "Vehicle", "Vessels Out", "Vessels Returned", "By", ...(canVesselEdit ? [""] : [])].map(h => (
                            <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147] whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F4F3F0]">
                        {movementLogs.map((log, i) => {
                          const totalOut = Object.values(log.vessels_out || {}).reduce((s, v) => s + (v || 0), 0);
                          const totalRet = Object.values(log.vessels_returned || {}).reduce((s, v) => s + (v || 0), 0);
                          const isDeleting = deletingId === log.id;
                          return (
                            <tr key={log.id || i} className={`hover:bg-[#FAFAF8] ${isDeleting ? "opacity-40" : ""}`} data-testid={`movement-row-${i}`}>
                              <td className="px-4 py-3 text-xs text-[#8A7E73] whitespace-nowrap">{fmtDateTime(log.date)}</td>
                              <td className="px-4 py-3 font-bold text-[#E8956D]">{log.route_number || "—"}</td>
                              <td className="px-4 py-3">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${log.direction === "outward" ? "bg-[#FDF4EB] text-[#E8956D]" : "bg-[#EDF2EE] text-[#637C64]"}`}>
                                  {log.direction}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${log.source === "gate" ? "bg-blue-50 text-blue-700" : log.source === "admin_correction" ? "bg-amber-50 text-amber-700" : "bg-[#F4F3F0] text-[#5C5147]"}`}>
                                  {log.source || "—"}
                                </span>
                              </td>
                              <td className="px-4 py-3 font-mono text-xs text-[#5C5147]">{log.vehicle_number || "—"}</td>
                              <td className="px-4 py-3 font-bold text-[#3A322C]">{totalOut > 0 ? totalOut : "—"}</td>
                              <td className="px-4 py-3 font-bold text-[#637C64]">{totalRet > 0 ? totalRet : "—"}</td>
                              <td className="px-4 py-3 text-xs text-[#8A7E73]">{log.created_by || "—"}</td>
                              {canVesselEdit && (
                                <td className="px-4 py-3">
                                  <button
                                    onClick={() => handleDeleteLogEntry(log)}
                                    disabled={isDeleting}
                                    className="p-1.5 rounded-lg text-[#C4BAAF] hover:text-[#C2624E] hover:bg-[#FBEAE7] transition-colors disabled:opacity-40"
                                    title="Delete this entry (admin only)"
                                    data-testid={`delete-movement-${i}`}
                                  >
                                    {isDeleting ? <RefreshCw size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}


          {/* ── INVENTORY & OPENING STOCK ── */}
          {activeView === "inventory" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#8A7E73]">Set opening stock for each vessel type. Track damaged / decommissioned vessels.</p>
                </div>
                {invLoading && <Loader size={16} className="animate-spin text-[#E8956D]" />}
              </div>

              <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F3F0]">
                    <tr>
                      {["Vessel Type", "Opening Stock", "Damaged / Removed", "Active Fleet", "Actions"].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F4F3F0]">
                    {inventory.map((inv, i) => {
                      const activeFleet = inv.current_stock;
                      return (
                        <tr key={`idx-${i}`} className="hover:bg-[#FAFAF8]">
                          <td className="px-4 py-3 font-bold text-[#3A322C]">{inv.label}</td>
                          <td className="px-4 py-3">
                            <span className="font-bold text-blue-600">{inv.opening_stock}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`font-bold ${inv.damaged_removed > 0 ? "text-[#C2624E]" : "text-[#C4BAAF]"}`}>
                              {inv.damaged_removed > 0 ? `-${inv.damaged_removed}` : "—"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xl font-extrabold ${activeFleet > 0 ? "text-[#637C64]" : "text-[#C4BAAF]"}`}>
                              {activeFleet}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => { setOpeningModal(inv); setOpeningForm({ opening_stock: String(inv.opening_stock || ""), notes: "" }); setOpeningErr(""); }}
                                className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-lg font-bold hover:bg-blue-100 flex items-center gap-1"
                                data-testid={`set-opening-${inv.size}`}>
                                <Package size={11} /> Set Opening Stock
                              </button>
                              {inv.current_stock > 0 && (
                                <button
                                  onClick={() => { setReduceModal(inv); setReduceForm({ quantity: "", reason: "Damaged", notes: "" }); setReduceErr(""); setReduceSuccess(""); }}
                                  className="text-xs bg-[#FBEAE7] text-[#C2624E] border border-[#C2624E]/30 px-3 py-1.5 rounded-lg font-bold hover:bg-[#C2624E]/10 flex items-center gap-1"
                                  data-testid={`reduce-stock-${inv.size}`}>
                                  <Wrench size={11} /> Report Damage / Remove
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Legend */}
              <div className="bg-[#F4F3F0] rounded-xl px-4 py-3 text-xs text-[#8A7E73] flex flex-wrap gap-4">
                <span><strong className="text-blue-600">Opening Stock</strong> — total vessels purchased/onboarded</span>
                <span><strong className="text-[#C2624E]">Damaged/Removed</strong> — permanently removed from fleet</span>
                <span><strong className="text-[#637C64]">Active Fleet</strong> = Opening Stock − Damaged/Removed</span>
              </div>
            </div>
          )}

          {/* ── BI-WEEKLY REPORT ── */}
          {activeView === "biweekly" && report && (
            <div className="space-y-4">
              <div className="bg-[#FDF4EB] border border-[#D98032]/30 rounded-xl p-4">
                <p className="text-sm font-bold text-[#D98032]">Bi-Weekly Period</p>
                <p className="text-xs text-[#8A7E73]">
                  {fmtDate(report.period_start)} → {fmtDate(report.period_end)}
                </p>
                {report.has_missing && (
                  <p className="text-sm text-[#C2624E] font-bold mt-2">Missing vessels — deduction required from transporter</p>
                )}
              </div>
              <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F4F3F0]">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">Route</th>
                        {Object.values(SIZE_LABELS).map(l => (
                          <React.Fragment key={l}>
                            <th className="text-center px-2 py-3 text-xs font-bold uppercase text-[#5C5147]">{l} Out</th>
                            <th className="text-center px-2 py-3 text-xs font-bold uppercase text-[#5C5147]">{l} Ret</th>
                            <th className="text-center px-2 py-3 text-xs font-bold uppercase text-[#C2624E]">{l} Mis</th>
                          </React.Fragment>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F4F3F0]">
                      {(report.by_route || []).map((r, i) => (
                        <tr key={`idx-${i}`} className={hasMissing(r) ? "bg-[#FBEAE7]/30" : "hover:bg-[#FAFAF8]"}>
                          <td className="px-4 py-3 font-bold text-[#E8956D]">Route {r.route_number}</td>
                          {Object.keys(SIZE_LABELS).map(key => (
                            <React.Fragment key={key}>
                              <td className="px-2 py-3 text-center text-[#5C5147]">{r.out?.[key] || 0}</td>
                              <td className="px-2 py-3 text-center text-[#637C64]">{r.returned?.[key] || 0}</td>
                              <td className="px-2 py-3 text-center font-bold text-[#C2624E]">{r.missing?.[key] || 0}</td>
                            </React.Fragment>
                          ))}
                        </tr>
                      ))}
                      <tr className="bg-[#F4F3F0] font-bold">
                        <td className="px-4 py-3 text-[#3A322C]">TOTAL</td>
                        {Object.keys(SIZE_LABELS).map(key => (
                          <React.Fragment key={key}>
                            <td className="px-2 py-3 text-center">{report.total_out?.[key] || 0}</td>
                            <td className="px-2 py-3 text-center text-[#637C64]">{report.total_returned?.[key] || 0}</td>
                            <td className="px-2 py-3 text-center text-[#C2624E]">{report.missing_total?.[key] || 0}</td>
                          </React.Fragment>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── STOCK HISTORY ── */}
          {activeView === "history" && (
            <div className="space-y-4">
              {logsLoading ? (
                <div className="flex justify-center py-10"><Loader size={20} className="animate-spin text-[#E8956D]" /></div>
              ) : stockLogs.length === 0 ? (
                <div className="text-center py-16 bg-white border border-[#E5E1DB] rounded-xl text-[#8A7E73]">
                  <Clock size={40} className="mx-auto mb-3 opacity-30" />
                  <p>No stock history yet. Set opening stock or report damage to see logs here.</p>
                </div>
              ) : (
                <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                  <div className="px-5 py-3 bg-[#F4F3F0] border-b border-[#E5E1DB]">
                    <p className="font-bold text-[#3A322C] text-sm">Vessel Stock Audit Trail</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F8F8F6]">
                        <tr>{["Timestamp", "Vessel", "Action", "Qty Changed", "Before → After", "Reason", "Notes", "By"].map(h => (
                          <th key={h} className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#5C5147]">{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody className="divide-y divide-[#F4F3F0]">
                        {stockLogs.map((log, i) => (
                          <tr key={`item-${i}`} className="hover:bg-[#FAFAF8]">
                            <td className="px-4 py-3 text-xs text-[#8A7E73] whitespace-nowrap">{fmtDateTime(log.timestamp)}</td>
                            <td className="px-4 py-3 font-bold text-[#3A322C] text-xs">{SIZE_LABELS[log.size] || log.size}</td>
                            <td className="px-4 py-3">
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${ACTION_COLORS[log.action_type] || "bg-gray-100 text-gray-600"}`}>
                                {log.action_type?.replace(/_/g, " ")}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-bold text-sm">
                              <span className={log.quantity_changed > 0 ? "text-[#637C64]" : "text-[#C2624E]"}>
                                {log.quantity_changed > 0 ? `+${log.quantity_changed}` : log.quantity_changed}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-[#5C5147]">{log.stock_before} → {log.stock_after}</td>
                            <td className="px-4 py-3 text-xs text-[#5C5147]">{log.reason || "—"}</td>
                            <td className="px-4 py-3 text-xs text-[#8A7E73] max-w-[150px] truncate">{log.notes || "—"}</td>
                            <td className="px-4 py-3 text-xs text-[#8A7E73]">{log.user_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── OPENING STOCK MODAL ── */}
      {openingModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-fade-in-up">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB]">
              <div>
                <h2 className="font-bold text-[#3A322C] font-[Manrope]">Set Opening Stock</h2>
                <p className="text-xs text-[#8A7E73] mt-0.5">Vessel: <strong>{openingModal.label}</strong></p>
              </div>
              <button onClick={() => setOpeningModal(null)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3 bg-[#F4F3F0] rounded-xl p-3 text-xs text-center">
                <div><p className="text-[#8A7E73] font-bold uppercase mb-1">Current Opening</p><p className="text-xl font-extrabold text-blue-600">{openingModal.opening_stock}</p></div>
                <div><p className="text-[#8A7E73] font-bold uppercase mb-1">Active Fleet</p><p className="text-xl font-extrabold text-[#637C64]">{openingModal.current_stock}</p></div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">New Opening Stock *</label>
                <input
                  type="number" min="0" step="1"
                  className="w-full border border-[#E5E1DB] rounded-xl px-4 py-3 text-lg font-bold outline-none focus:border-[#E8956D] text-center"
                  value={openingForm.opening_stock}
                  onChange={e => setOpeningForm(f => ({ ...f, opening_stock: e.target.value }))}
                  placeholder="e.g. 500"
                  data-testid="opening-stock-input"
                />
                <p className="text-xs text-[#8A7E73] mt-1">Active Fleet = Opening Stock − Damaged/Removed ({openingModal.damaged_removed || 0})</p>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes (optional)</label>
                <textarea rows={2} placeholder="e.g. Initial onboarding count, purchased 200 new vessels..."
                  className="w-full border border-[#E5E1DB] rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none"
                  value={openingForm.notes} onChange={e => setOpeningForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              {openingErr && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{openingErr}</p>}
              <div className="flex gap-3">
                <button onClick={() => setOpeningModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
                <button onClick={handleSetOpeningStock} disabled={openingLoading}
                  className="flex-1 bg-blue-600 text-white rounded-lg py-2.5 text-sm font-bold disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid="confirm-opening-stock-btn">
                  {openingLoading ? <Loader size={14} className="animate-spin" /> : <Package size={14} />} Save Opening Stock
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── REDUCE STOCK / DAMAGE MODAL ── */}
      {reduceModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-fade-in-up">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB]">
              <div>
                <h2 className="font-bold text-[#3A322C] font-[Manrope]">Report Damage / Remove Stock</h2>
                <p className="text-xs text-[#8A7E73] mt-0.5">Vessel: <strong>{reduceModal.label}</strong> · Active fleet: <strong className="text-[#637C64]">{reduceModal.current_stock}</strong></p>
              </div>
              <button onClick={() => setReduceModal(null)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              {reduceSuccess ? (
                <div className="bg-[#EDF2EE] border border-[#637C64]/30 rounded-xl px-4 py-4 text-sm text-[#637C64] font-bold text-center">
                  <CheckCircle size={24} className="mx-auto mb-2" />{reduceSuccess}
                </div>
              ) : (
                <>
                  <div>
                    <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Quantity to Remove *</label>
                    <input
                      type="number" min="1" max={reduceModal.current_stock} step="1"
                      className="w-full border border-[#E5E1DB] rounded-xl px-4 py-3 text-lg font-bold outline-none focus:border-[#E8956D] text-center"
                      value={reduceForm.quantity}
                      onChange={e => setReduceForm(f => ({ ...f, quantity: e.target.value }))}
                      placeholder={`Max: ${reduceModal.current_stock}`}
                      data-testid="reduce-quantity-input"
                    />
                    {reduceForm.quantity && parseInt(reduceForm.quantity) > reduceModal.current_stock && (
                      <p className="text-xs text-[#C2624E] mt-1 font-bold">Cannot exceed active fleet of {reduceModal.current_stock}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Reason for Removal *</label>
                    <select
                      className="w-full border border-[#E5E1DB] rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                      value={reduceForm.reason} onChange={e => setReduceForm(f => ({ ...f, reason: e.target.value }))}
                      data-testid="reduce-reason-select">
                      {REMOVAL_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes (optional)</label>
                    <textarea rows={3} placeholder="Describe the damage, loss location, or other details..."
                      className="w-full border border-[#E5E1DB] rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none"
                      value={reduceForm.notes} onChange={e => setReduceForm(f => ({ ...f, notes: e.target.value }))} />
                  </div>
                  {reduceErr && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{reduceErr}</p>}
                  <div className="flex gap-3">
                    <button onClick={() => setReduceModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
                    <button onClick={handleReduceStock} disabled={reduceLoading || !reduceForm.quantity || parseInt(reduceForm.quantity) > reduceModal.current_stock}
                      className="flex-1 bg-[#C2624E] text-white rounded-lg py-2.5 text-sm font-bold disabled:opacity-60 flex items-center justify-center gap-2"
                      data-testid="confirm-reduce-stock-btn">
                      {reduceLoading ? <Loader size={14} className="animate-spin" /> : <Wrench size={14} />} Confirm Removal
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
